import { GenericObjectType } from "@rjsf/utils";

/**
 * The internal representation of a single derived field rule.
 */
export type DerivedRule<T extends GenericObjectType = GenericObjectType> = {
  getValue: (values: T) => unknown;
  isActive: boolean;
  /**
   * The last value written to this field by the rule (or the initial form value when the rule
   * was first set up). Used to detect manual edits that happened in a *previous* update cycle,
   * before the derivation rule ever fired.
   */
  syncedValue: unknown;
  /**
   * The form value at the time the hook was initialised. When the rule returns `undefined`
   * after previously setting the field, the field is reset back to this value.
   */
  initialValue: unknown;
};

/**
 * Maps a form data type to a derivation rules structure.
 *
 * - For scalar fields (string, number, boolean, etc.): a function that receives
 *   the FULL form data and returns the derived value.
 * - For object fields: either a nested `FormDerivationRulesFor` (to derive individual
 *   sub-fields), or a function that returns the entire sub-object.
 *
 * The function always receives the ROOT form data, not the nested sub-object.
 *
 * @example
 * const rules: FormDerivationRulesFor<ProjectFormValues> = {
 *   code: (data) => data.name.toLowerCase().replace(/\s+/g, "-"),
 * };
 *
 * @example (nested paths)
 * const rules: FormDerivationRulesFor<ProcessFormData> = {
 *   outputs: {
 *     name: (data) => `${data.inputs.studyArea.id}-derived`,
 *   },
 * };
 *
 * Returning undefined will reset the field to its initial value (the value it had when the form was first rendered).
 */
export type FormDerivationRulesFor<T, Root = T> = {
  [K in keyof T]?: NonNullable<T[K]> extends object
    ? FormDerivationRulesFor<NonNullable<T[K]>, Root> | ((formData: Root) => T[K] | undefined)
    : (formData: Root) => T[K] | undefined;
};

/**
 * Converts a nested `FormDerivationRulesFor` object into a flat path-keyed record.
 * Functions encountered at any depth become rules keyed by their dot-notation path.
 *
 * @example
 * flattenDerivedRules({ outputs: { name: fn } })
 * // → { "outputs.name": { getValue, isActive: true } }
 */
export const flattenDerivedRules = <FormData extends GenericObjectType>(
  formDerivations: FormDerivationRulesFor<FormData>,
  initialValues: FormData
): Record<string, DerivedRule> => {
  const rules: Record<string, DerivedRule> = {};

  const walk = (node: unknown, values: unknown, path: string) => {
    if (typeof node === "function") {
      rules[path] = {
        getValue: node as (values: GenericObjectType) => unknown,
        isActive: true,
        syncedValue: values,
        initialValue: values,
      };
    } else if (node && typeof node === "object") {
      for (const [key, value] of Object.entries(node)) {
        const nextValues =
          values && typeof values === "object" ? (values as GenericObjectType)[key] : undefined;
        walk(value, nextValues, path ? `${path}.${key}` : key);
      }
    }
  };

  walk(formDerivations, initialValues, "");
  return rules;
};
